/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable no-plusplus */
import React, { useEffect, useState } from "react";
import {
  Button,
  Divider,
  Drawer,
  Group,
  Input,
  InputWrapper,
  NumberInput,
  Select,
  SimpleGrid,
  Space,
  Switch,
  Tabs,
  Text,
  createStyles,
} from "@mantine/core";
import { Trash2 } from "lucide-react";
import { DatePicker, DateRangePicker } from "@mantine/dates";
import { RRule } from "rrule";
import {
  IBlockedDateEmployees,
  IEmployeeOdpBlockDate,
  OdpBlockedDateContextType,
} from "../../@types/blockedDates";
import {
  OdpBlockedDatesContext,
  weekdaysOptions,
} from "../../providers/BlockedDatesProvider";
import AuditTimestamps from "./AuditTimestamps";
import { BlockedDatesAppliedSuccessfully } from "./BlockedDatesAppliedSuccessfully";
import EmployeesBlocked from "./EmployeesBlocked";
import { ConfirmUpdateBlockedDates } from "./ConfirmUpdateBlockedDates";

const useStyles = createStyles((theme) => ({
  blockedDatesCTA: {
    position: "absolute",
    bottom: "0",
    left: "0",
    padding: "0 10px 10px 10px",
    boxSizing: "border-box",
    width: "100%",
    [`@media (max-width: ${theme.breakpoints.xs}px)`]: {
      padding: "0",
      position: "initial",
    },
  },
  byWeekDay: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    borderRadius: "50%",
    height: 42,
    width: 42,
    cursor: "pointer",
    background: "rgba(6, 33, 51, 0.10)",
    border: "none",
  },
}));

const isEmployeeEditValid = (
  isDeleting: boolean,
  isEmployeeBlockedDate: boolean,
  employeesCreate: IBlockedDateEmployees[]
): boolean => {
  if (isDeleting) {
    return true;
  }

  if (!isEmployeeBlockedDate) {
    return true;
  }
  if (employeesCreate.length === 0) {
    return false;
  }
  return true;
};

export const updateButtonOnClick = (
  isEmployeeBlockedDate: boolean,
  employeesCreate: IBlockedDateEmployees[],
  employeesDelete: IBlockedDateEmployees[],
  currentBlockedDate: IEmployeeOdpBlockDate,
  updateOrCreateNewBlockDates: any,
  updateOrCreateEmployeesBlockedDates: any,
  setIsConfirmingUpdate: (val: boolean) => void
): void => {
  setIsConfirmingUpdate(false);
  if (isEmployeeBlockedDate) {
    updateOrCreateEmployeesBlockedDates({
      employeesCreate,
      employeesDelete,
      blockedDateId: currentBlockedDate.id,
    });
  } else {
    updateOrCreateNewBlockDates();
  }
};

type Props = {
  openedEditBlockedDateDrawer: boolean;
  setOpenedEditBlockedDateDrawer: (val: boolean) => void;
  setIsDeleteBlockedDatesModalOpened: (val: boolean) => void;
};

const EditBlockedDateDrawer: React.FC<Props> = ({
  openedEditBlockedDateDrawer,
  setOpenedEditBlockedDateDrawer,
  setIsDeleteBlockedDatesModalOpened,
}) => {
  const [activeTab, setActiveTab] = useState<number>(0);

  const [employeesCreate, setEmployeesCreate] = useState<
    IBlockedDateEmployees[]
  >([]);
  const [employeesDelete, setEmployeesDelete] = useState<
    IBlockedDateEmployees[]
  >([]);

  const [endDayOptions] = useState(
    Array.from({ length: 31 }, (_, index) => index + 1).map((n) => ({
      label: n.toString(),
      value: n.toString(),
    }))
  );

  const startDayOptions = Array.from(
    { length: 31 },
    (_, index) => index + 1
  ).map((n) => ({
    label: n.toString(),
    value: n.toString(),
  }));

  const { classes } = useStyles();

  const {
    setRecurrenceEndDate,
    setByMonthDayStart,
    setByMonthDayEnd,
    frequencyValue,
    setRepeatInterval,
    frequencyOptions,
    toggleByWeekDay,
    setNewDateRange,
    currentBlockedDate,
    recurrenceEndDate,
    byMonthDayStart,
    byMonthDayEnd,
    updateOrCreateNewBlockDates,
    updateOrCreateEmployeesBlockedDates,
    isUpdatedSuccessfully,
    setIsUpdatedSuccessfully,
    setNewTitle,
    blockReasons,
    setBlockReason,
    odpDateBoundaries,
    byWeekDays,
    isValidData,
    setFrequencyValue,
    setOdpDateBoundaries,
    repeatInterval,
    isUpdatingLoading,
  } = React.useContext(OdpBlockedDatesContext) as OdpBlockedDateContextType;

  const [isDeleting, setIsDeleting] = useState<boolean>(false);

  const [isEmployeeBlockedDate, setIsEmployeeBlockedDate] =
    useState<boolean>(false);

  const [isBlockDateRepeating, setIsBlockDateRepeating] =
    useState<boolean>(false);

  const [isConfirmingUpdate, setIsConfirmingUpdate] = useState(false);

  const setDefaultValue = (checked: boolean): void => {
    // reset
    setOdpDateBoundaries({
      start: null,
      end: null,
    });
    setRepeatInterval(1);

    setRecurrenceEndDate(null);

    if (checked) {
      setFrequencyValue(RRule.YEARLY);
    } else {
      setFrequencyValue(RRule.DAILY);
    }
  };

  const getFrequencyValue = (): string => {
    if (frequencyValue === RRule.MONTHLY) {
      return RRule.MONTHLY.toString();
    }
    if (frequencyValue === RRule.WEEKLY) {
      return RRule.WEEKLY.toString();
    }
    return RRule.YEARLY.toString();
  };

  useEffect(() => {
    if (frequencyValue === RRule.DAILY) {
      setIsBlockDateRepeating(false);
    } else {
      setIsBlockDateRepeating(true);
    }
  }, [openedEditBlockedDateDrawer]);

  useEffect(() => {
    if (!openedEditBlockedDateDrawer) {
      setEmployeesDelete([]);
    }
  }, [openedEditBlockedDateDrawer]);

  useEffect(() => {
    if (
      currentBlockedDate.employees &&
      currentBlockedDate.employees.length > 0
    ) {
      setIsEmployeeBlockedDate(true);
      setEmployeesCreate(currentBlockedDate.employees);
      setEmployeesDelete([]);
    } else {
      setIsEmployeeBlockedDate(false);
      setEmployeesCreate([]);
      setEmployeesDelete([]);
    }
  }, [currentBlockedDate]);

  return (
    <Drawer
      title={
        <Group>
          <Text
            size="xl"
            style={{
              fontSize: "18px",
              fontWeight: 700,
              lineHeight: "24.59px",
            }}
          >
            Edit Block Transaction Dates
          </Text>
        </Group>
      }
      opened={openedEditBlockedDateDrawer}
      size="xl"
      onClose={() => {
        setOpenedEditBlockedDateDrawer(false);
      }}
      styles={{
        header: {
          backgroundColor: "#ffffff",
          borderTopLeftRadius: "15px",
          borderBottomLeftRadius: "15px",
        },
        drawer: {
          backgroundColor: "#ffffff",
          borderTopLeftRadius: "15px",
          borderBottomLeftRadius: "15px",
        },
      }}
      overlayOpacity={0.1}
      overlayBlur={3}
      position="right"
    >
      <ConfirmUpdateBlockedDates
        isOpen={isConfirmingUpdate}
        setOpen={setIsConfirmingUpdate}
        lang={{
          title: "Update Blocked Dates",
          description: "Are you sure you want to set your block dates?",
          confirm: "Yes, Update",
          cancel: "No, Don't Update",
        }}
        updateBlockedDate={() => {
          updateButtonOnClick(
            isEmployeeBlockedDate,
            employeesCreate,
            employeesDelete,
            currentBlockedDate,
            updateOrCreateNewBlockDates,
            updateOrCreateEmployeesBlockedDates,
            setIsConfirmingUpdate
          );
        }}
      />

      <BlockedDatesAppliedSuccessfully
        isOpen={isUpdatedSuccessfully && !isDeleting}
        setIsOpen={setIsUpdatedSuccessfully}
        setIsCreateNew={setOpenedEditBlockedDateDrawer}
        lang={{
          title: "Block Dates Applied Successfully",
          subtitle: "Your block dates are active.",
          description:
            "Employees won't be able to take advances during the set dates.",
        }}
      />
      <Tabs
        px={41}
        active={activeTab}
        onTabChange={(value) => {
          setActiveTab(value);
        }}
        styles={{
          tabLabel: {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            fontWeight: "bold !important",
          },
          tabControl: {
            padding: 0,
          },
        }}
      >
        <Tabs.Tab label="Edit Dates">
          {/* Date Title Input */}
          <div style={{ marginTop: "32px" }}>
            <Text
              style={{
                fontSize: "14px",
                fontWeight: 600,
                lineHeight: "20px",
                marginBottom: "8px",
              }}
            >
              <span>Title</span>
              <span style={{ color: "#ED7687" }}> *</span>
            </Text>
            <InputWrapper id="input-limit" required style={{ width: "100%" }}>
              <Input
                id="input-limit"
                type="text"
                onChange={(e) => setNewTitle(e.target.value)}
                value={currentBlockedDate?.title || ""}
              />
            </InputWrapper>
          </div>

          {/* Creation / Update Timestamps */}
          {(currentBlockedDate?.created_at ||
            currentBlockedDate?.updated_at) && (
            <div style={{ marginTop: 12, marginBottom: 4 }}>
              <AuditTimestamps
                created_at={currentBlockedDate.created_at}
                created_by_name={currentBlockedDate.created_by_name}
                updated_at={currentBlockedDate.updated_at}
                updated_by_name={currentBlockedDate.updated_by_name}
              />
            </div>
          )}

          {/* isRepeating? */}
          <span
            style={{
              lineHeight: "16px",
              marginTop: "32px",
              marginBottom: "8px",
              display: "flex",
              alignItems: "center",
            }}
          >
            <Text
              style={{
                fontSize: "14px",
                fontWeight: 600,
              }}
            >
              Recurring Dates Setup
            </Text>
          </span>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Text
              style={{
                fontSize: "12px",
                fontWeight: 500,
                lineHeight: "20px",
              }}
            >
              Repeat block dates?
            </Text>
            <Switch
              checked={isBlockDateRepeating}
              onChange={(s) => {
                setIsBlockDateRepeating(s.target.checked);
                setDefaultValue(s.target.checked);
              }}
              size="sm"
            />
          </div>

          {/* Reason for Block */}
          {blockReasons.length > 0 && (
            <div style={{ marginTop: "32px" }}>
              <Text
                style={{
                  fontSize: "14px",
                  fontWeight: 600,
                  lineHeight: "20px",
                  marginBottom: "8px",
                }}
              >
                <span>Reason for Block?</span>
                <span style={{ color: "#ED7687" }}> *</span>
              </Text>
              <Select
                data={blockReasons
                  .filter((order) => order.attributes.Active)
                  .sort((a, b) => a.attributes.Order - b.attributes.Order)
                  .map((r) => r.attributes.Reason)}
                required
                id="block-reason"
                defaultValue={
                  currentBlockedDate?.block_reason ??
                  blockReasons[0].attributes.Reason
                }
                onChange={(val) => setBlockReason(val ?? "")}
              />
            </div>
          )}

          {/* Once OFF Dates */}
          {!isBlockDateRepeating && frequencyValue === RRule.DAILY && (
            <>
              <Text
                style={{
                  fontSize: "14px",
                  fontWeight: 600,
                  lineHeight: "20px",
                  marginBottom: "8px",
                  marginTop: "32px",
                }}
              >
                <span>Select Blocked Dates</span>
                <span style={{ color: "#ED7687" }}> *</span>
              </Text>
              <InputWrapper>
                <DateRangePicker
                  allowLevelChange={false}
                  allowSingleDateInRange
                  placeholder="DD/MM/YYYY"
                  value={[odpDateBoundaries.start, odpDateBoundaries.end]}
                  onChange={(range) => {
                    setNewDateRange(range);
                  }}
                />
              </InputWrapper>
            </>
          )}

          {/* OCCURANCE */}
          {isBlockDateRepeating && (
            <div style={{ paddingLeft: "12px" }}>
              <Space h="xl" />

              <Group>
                <Text
                  style={{
                    fontSize: "12px",
                    fontWeight: 500,
                    lineHeight: "20px",
                  }}
                >
                  <span>Repeat Every</span>
                  <span style={{ color: "#ED7687" }}> *</span>
                </Text>

                <NumberInput
                  hideControls
                  style={{ width: 42 }}
                  defaultValue={repeatInterval}
                  onChange={(i: number) => setRepeatInterval(i)}
                />

                <Select
                  onChange={(val: string) => {
                    setFrequencyValue(Number(val));
                  }}
                  defaultValue={getFrequencyValue()}
                  data={frequencyOptions}
                />

                {repeatInterval > 1 && frequencyValue === RRule.YEARLY && (
                  <Text
                    style={{
                      fontSize: "12px",
                      color: "rgba(6, 33, 51, 0.40)",
                      marginTop: "-8px",
                    }}
                  >
                    By selecting a repeat {repeatInterval} times a year, it will
                    occur every {repeatInterval} years. For example, if you
                    choose to repeat 2 times a year, the event will take place
                    every 2nd year. For more frequent repeats within a year,
                    consider choosing monthly to meet your desired needs.
                  </Text>
                )}

                {repeatInterval > 1 && frequencyValue === RRule.MONTHLY && (
                  <Text
                    style={{
                      fontSize: "12px",
                      color: "rgba(6, 33, 51, 0.40)",
                      marginTop: "-8px",
                    }}
                  >
                    By selecting a repeat {repeatInterval} times a month, it
                    will occur every {repeatInterval} months. For example, if
                    you choose to repeat 2 times a month, the event will take
                    place every 2nd month. For more frequent repeats within a
                    year, consider choosing weekly to meet your desired needs.
                  </Text>
                )}
              </Group>
            </div>
          )}

          {/* YEARLY */}
          {isBlockDateRepeating && frequencyValue === RRule.YEARLY && (
            <div style={{ paddingLeft: "12px" }}>
              <Space h="xl" />

              <Text
                style={{
                  fontSize: "12px",
                  fontWeight: 500,
                  lineHeight: "20px",
                  marginBottom: "8px",
                }}
              >
                <span>Select Blocked Dates</span>
                <span style={{ color: "#ED7687" }}> *</span>
              </Text>

              <InputWrapper required>
                <DateRangePicker
                  labelFormat="MMMM"
                  inputFormat="MMMM DD"
                  allowSingleDateInRange
                  placeholder="DD/MM/YYYY"
                  value={[odpDateBoundaries.start, odpDateBoundaries.end]}
                  onChange={(range) => {
                    setNewDateRange(range);
                    // setCurrentDateRange(range);
                  }}
                  onDropdownClose={() => {
                    // updateDateRange();
                  }}
                />
              </InputWrapper>
            </div>
          )}

          {/* MONTHLY */}
          {isBlockDateRepeating && frequencyValue === RRule.MONTHLY && (
            <div style={{ paddingLeft: "12px" }}>
              <Space h="xl" />
              <Group>
                <Text style={{ fontSize: "12px", fontWeight: 500 }}>
                  <span>Start</span>
                  <span style={{ color: "#ED7687" }}> *</span>
                </Text>
                <div style={{ width: 64 }}>
                  <Select
                    onChange={(opt) => setByMonthDayStart(Number(opt))}
                    defaultValue={byMonthDayStart.toString()}
                    data={startDayOptions}
                  />
                </div>

                <Text style={{ fontSize: "12px", fontWeight: 500 }}>
                  End <span style={{ color: "#ED7687" }}> *</span>
                </Text>
                <div style={{ width: 64 }}>
                  <Select
                    onChange={(opt) => {
                      setByMonthDayEnd(Number(opt));
                      // setMonthlyEndDate(opt);
                    }}
                    defaultValue={byMonthDayEnd.toString()}
                    data={endDayOptions}
                  />
                </div>
              </Group>
            </div>
          )}

          {/* WEEKLY */}
          {isBlockDateRepeating && frequencyValue === RRule.WEEKLY && (
            <div style={{ paddingLeft: "12px" }}>
              <Space h="xl" />
              <Text
                style={{
                  fontSize: "12px",
                  fontWeight: 500,
                  lineHeight: "20px",
                  marginBottom: "8px",
                }}
              >
                <span>Repeat On</span>
                <span style={{ color: "#ED7687" }}> *</span>
              </Text>

              <Group>
                <SimpleGrid cols={7}>
                  {weekdaysOptions.map((day) => (
                    <button
                      key={day.label}
                      className={classes.byWeekDay}
                      onClick={() => {
                        toggleByWeekDay(day.rule);
                      }}
                      style={
                        byWeekDays.includes(day.rule)
                          ? {
                              backgroundColor: "#ED7687",
                              color: "white",
                            }
                          : {}
                      }
                    >
                      {day.label}
                    </button>
                  ))}
                </SimpleGrid>
              </Group>
            </div>
          )}

          {/* END DATE */}
          {isBlockDateRepeating && (
            <div style={{ paddingLeft: "12px" }}>
              <Space h="xl" />
              <Text
                style={{
                  fontSize: "12px",
                  fontWeight: 500,
                  marginBottom: "8px",
                }}
              >
                <span>Set End Date for Recurring Blocked Dates</span>
                <span style={{ color: "#ED7687" }}> *</span>
              </Text>

              <Text
                style={{
                  fontSize: "12px",
                  color: "rgba(6, 33, 51, 0.40)",
                }}
              >
                Specify the date when a recurring EWA block comes to an end. By
                setting the end date, you ensure it no longer extends beyond the
                specified end date
              </Text>

              <DatePicker
                placeholder="Specify End Date"
                required
                defaultValue={recurrenceEndDate}
                onChange={(endDate: Date) => setRecurrenceEndDate(endDate)}
                style={{ marginTop: "8px" }}
              />
            </div>
          )}

          {/* Delete Date Toogle */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              flexDirection: "column",
              marginTop: "23px",
            }}
          >
            <span
              style={{
                lineHeight: "16px",
                marginBottom: "8px",
                marginTop: "10px",
                display: "flex",
                alignItems: "center",
              }}
            >
              <Text
                style={{
                  fontSize: "14px",
                  fontWeight: 600,
                }}
              >
                Employee-Specific Restrictions
              </Text>
            </span>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Text
                style={{
                  fontSize: "12px",
                  fontWeight: 500,
                  lineHeight: "20px",
                }}
              >
                Restrict Access for Specific Employees?
              </Text>
              <Switch
                size="sm"
                checked={isEmployeeBlockedDate}
                onChange={(s) => setIsEmployeeBlockedDate(s.target.checked)}
              />
            </div>
            <Space h="xl" />

            {openedEditBlockedDateDrawer && employeesCreate && (
              <>
                <EmployeesBlocked
                  isEmployeeBlockedDate={isEmployeeBlockedDate}
                  employeesCreate={employeesCreate}
                  employeesDelete={employeesDelete}
                  setEmployeesCreate={setEmployeesCreate}
                  setEmployeesDelete={setEmployeesDelete}
                />
                <Space h="xl" />
              </>
            )}
            <div>
              <span
                style={{
                  lineHeight: "16px",
                  marginBottom: "8px",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <Trash2 size={14} />
                <Text
                  style={{
                    fontSize: "14px",
                    fontWeight: 600,
                    marginLeft: "8px",
                  }}
                >
                  Delete Block
                </Text>
              </span>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Text
                  style={{
                    fontSize: "12px",
                    fontWeight: 500,
                    lineHeight: "20px",
                  }}
                >
                  Do you want to delete this blocked date?
                </Text>

                <Switch
                  size="sm"
                  checked={isDeleting}
                  onChange={(s) => setIsDeleting(s.target.checked)}
                />
              </div>
            </div>
            <Space h="xl" />
            <Divider mt={50} />
            <Space h="xl" />
          </div>

          {/* Drawer Actions */}
          <div
            style={
              isBlockDateRepeating
                ? {
                    display: "flex",
                    flexWrap: "wrap",
                    justifyContent: "space-between",
                    marginTop: "100px",
                    marginBottom: "50px",
                    width: "100%",
                  }
                : {
                    display: "flex",
                    flexWrap: "wrap",
                    justifyContent: "space-evenly",
                    bottom: "50px",
                    marginBottom: "50px",
                    left: "0",
                    width: "100%",
                  }
            }
          >
            <Button
              variant="outline"
              style={{
                height: "40px",
                padding: "0 20px",
                width: "200px",
              }}
              onClick={() => {
                setOpenedEditBlockedDateDrawer(false);
              }}
            >
              Cancel
            </Button>
            <Button
              loading={isUpdatingLoading}
              disabled={
                !(
                  isValidData() &&
                  isEmployeeEditValid(
                    isDeleting,
                    isEmployeeBlockedDate,
                    employeesCreate
                  )
                )
              }
              onClick={() =>
                isDeleting
                  ? setIsDeleteBlockedDatesModalOpened(true)
                  : setIsConfirmingUpdate(true)
              }
              style={{
                height: "40px",
                padding: "0 20px",
                width: "200px",
              }}
            >
              Update
            </Button>
          </div>
        </Tabs.Tab>
      </Tabs>
    </Drawer>
  );
};
export default EditBlockedDateDrawer;
